import React, { useMemo, useState } from 'react';
import { useDraftAccountCache } from '../../api/useDraftAccountQuery';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { VisualPickerButton } from '../../../../CommonComponents/OPL/VisualPickerButton/VisualPickerButton';
import {
  AccountOpeningDraftDTO,
  CustomerType,
  IraAccountType,
  JointAccountType,
} from '../../AccountOpening.meta';
import { Footer } from '../../components/Footer/Footer';
import { VisualPickerButtonContainer } from '../../../../CommonComponents/OPL/VisualPickerButton/VisualPickerButtonContainer';
import LongTermSVG from '../../../../../img/autoAccountWorkflow/longTerm.svg';
import RetirementSVG from '../../../../../img/autoAccountWorkflow/retirement.svg';
import { AccountType } from './AccountSelection.meta';
import { SimpleIraPlanName } from '../SimpleIraPlanName/SimpleIraPlanName';
import { StepCard } from '../../components/StepCard/StepCard';
import { StepContent } from '../../components/StepContent/StepContent';
import { GliaGlobalStyle } from '../../AccountOpening.styles';

interface Props {
  data: AccountOpeningDraftDTO;
}

export const AccountSelection = ({ data }: Props) => {
  const { data: draftAccount, options } = data;

  const { accountIraType } = draftAccount;

  const initialSelectedType = useMemo(() => {
    if (
      !(
        options.allowedConsumerTypes.includes(CustomerType.Individual) ||
        options.allowedConsumerTypes.includes(CustomerType.Joint)
      )
    ) {
      return AccountType.Retirement;
    }

    if (!options.allowedConsumerTypes.includes(CustomerType.Ira)) {
      return AccountType.LongTerm;
    }

    return null;
  }, [options.allowedConsumerTypes]);

  const [selectedType, setSelectedType] = useState<AccountType | null>(initialSelectedType);

  const { mutateDraftAccount } = useDraftAccountCache();

  const renderContent = () => {
    if (selectedType && selectedType === AccountType.LongTerm) {
      return (
        <>
          <Text color="neutral.700" fontSize="h3" fontWeight={500} mb="32">
            Who is this account for?
          </Text>

          <Box display="flex" flexDirection="column">
            {options.allowedConsumerTypes.includes(CustomerType.Individual) && (
              <VisualPickerButton
                onClick={() =>
                  mutateDraftAccount({
                    customerType: CustomerType.Individual,
                    jointAccountType: JointAccountType.JointRightsOfSurvivorship,
                  })
                }
              >
                Me
              </VisualPickerButton>
            )}

            {options.allowedConsumerTypes.includes(CustomerType.Joint) && (
              <VisualPickerButton
                onClick={() => {
                  mutateDraftAccount({
                    customerType: CustomerType.Joint,
                    jointAccountType: JointAccountType.JointRightsOfSurvivorship,
                  });
                }}
                mt="16"
              >
                Me and my Partner
              </VisualPickerButton>
            )}
          </Box>
        </>
      );
    }

    if (selectedType && selectedType === AccountType.Retirement) {
      return (
        <>
          <Text color="neutral.700" fontSize="h3" fontWeight={500} mb="32">
            What type of retirement account is right for you?
          </Text>

          <Box display="flex" flexDirection="column">
            {options.allowedIraAccountTypes.includes(IraAccountType.TraditionalIra) && (
              <VisualPickerButton
                onClick={() => {
                  mutateDraftAccount({
                    customerType: CustomerType.Ira,
                    accountIraType: IraAccountType.TraditionalIra,
                  });
                }}
                helperText="Contributions are tax-deductible, earnings grow tax-free, and withdrawals are subject to income tax."
              >
                Traditional IRA
              </VisualPickerButton>
            )}

            {options.allowedIraAccountTypes.includes(IraAccountType.RothIra) && (
              <VisualPickerButton
                onClick={() => {
                  mutateDraftAccount({
                    customerType: CustomerType.Ira,
                    accountIraType: IraAccountType.RothIra,
                  });
                }}
                helperText="Contributions are taxed, and then earnings and withdrawals are tax-free. Qualifying conditions apply."
                mt="16"
              >
                Roth IRA
              </VisualPickerButton>
            )}

            {options.allowedIraAccountTypes.includes(IraAccountType.RolloverIra) && (
              <VisualPickerButton
                onClick={() => {
                  mutateDraftAccount({
                    customerType: CustomerType.Ira,
                    accountIraType: IraAccountType.RolloverIra,
                  });
                }}
                helperText="Move a previous retirement account."
                mt="16"
              >
                Roll over a 401(k) to an IRA
              </VisualPickerButton>
            )}

            {options.allowedIraAccountTypes.includes(IraAccountType.SimpleIraParticipant) && (
              <VisualPickerButton
                onClick={() => {
                  mutateDraftAccount({
                    accountIraType: IraAccountType.SimpleIraParticipant,
                  });
                }}
                helperText="Pre-tax contribution. Reduces your taxable income, providing a tax benefit today."
                mt="16"
              >
                Simple IRA
              </VisualPickerButton>
            )}
          </Box>
        </>
      );
    }

    return (
      <>
        <Text color="neutral.700" fontSize="h3" fontWeight={500} mb="32">
          What’s your main financial goal?
        </Text>

        <Box display="flex" justifyContent="space-between" gridGap={16}>
          <Box flex={1}>
            <VisualPickerButtonContainer onClick={() => setSelectedType(AccountType.LongTerm)}>
              <Box display="flex" flexDirection="column" p="8" alignItems="center">
                <LongTermSVG />

                <Text color="neutral.700" fontWeight={500} mt="16" textAlign="center">
                  I’m investing for the long term
                </Text>

                <Text color="neutral.500" fontSize="small" mt="4" textAlign="center">
                  A flexible account designed for longer term saving will get you started.
                </Text>
              </Box>
            </VisualPickerButtonContainer>
          </Box>

          <Box flex={1}>
            <VisualPickerButtonContainer onClick={() => setSelectedType(AccountType.Retirement)}>
              <Box display="flex" flexDirection="column" p="8" alignItems="center">
                <RetirementSVG />

                <Text color="neutral.700" fontWeight={500} mt="16" textAlign="center">
                  I’m saving for retirement
                </Text>

                <Text color="neutral.500" fontSize="small" mt="4" textAlign="center">
                  A Traditional IRA, Roth IRA or a 401(k)-to-IRA rollover allows your savings to
                  grow tax-free.
                </Text>
              </Box>
            </VisualPickerButtonContainer>
          </Box>
        </Box>
      </>
    );
  };

  if (accountIraType === IraAccountType.SimpleIraParticipant) {
    return <SimpleIraPlanName draftAccount={draftAccount} />;
  }

  return (
    <>
      <GliaGlobalStyle />

      <Box minWidth={864} mt={96}>
        <StepContent>
          <StepCard>{renderContent()}</StepCard>
        </StepContent>

        <Footer
          onPrevious={
            selectedType && !initialSelectedType ? () => setSelectedType(null) : undefined
          }
          showNext={false}
        />
      </Box>
    </>
  );
};
